import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Collapse,
  List,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import {
  DashboardOutlined,
  WhatsApp,
  SettingsOutlined,
  PeopleAltOutlined,
  ContactPhoneOutlined,
  AccountTreeOutlined,
  FlashOn,
  HelpOutline,
  CodeRounded,
  Schedule,
  LocalOffer,
  EventAvailable,
  ExpandLess,
  ExpandMore,
  People,
  ListAlt,
  Announcement,
  Forum,
  LocalAtm,
  Business,
  AllInclusiveRounded,
  AttachFileRounded,
  DescriptionRounded,
  DeviceHubRounded,
  GridOnRounded,
  PhonelinkSetupRounded,
  ViewKanban,
  Campaign,
  WebhookRounded,
  ShapeLineRounded,
  TrendingUp,
  PersonAdd,
  Facebook,
  Instagram,
  Analytics,
  MonetizationOn,
  Group,
  BarChart,
  SmartToy,
} from "@mui/icons-material";
import SignalCellularConnectedNoInternet4BarTwoTone from "@mui/icons-material/SignalCellularConnectedNoInternet4BarTwoTone";

import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useActiveMenu } from "../context/ActiveMenuContext";
import { Can } from "../components/Can";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";
import { i18n } from "../translate/i18n";
import useHelps from "../hooks/useHelps";

// ---------------- Icon palette ----------------
const iconStyles = {
  dashboard: { color: "#6366f1", gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)" },
  tickets: { color: "#10b981", gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
  messages: { color: "#f59e0b", gradient: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)" },
  kanban: { color: "#8b5cf6", gradient: "linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%)" },
  contacts: { color: "#06b6d4", gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)" },
  schedules: { color: "#ec4899", gradient: "linear-gradient(135deg, #ec4899 0%, #be185d 100%)" },
  tags: { color: "#14b8a6", gradient: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)" },
  chats: { color: "#f97316", gradient: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)" },
  helps: { color: "#3b82f6", gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" },
  campaigns: { color: "#ef4444", gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" },
  flowbuilder: { color: "#84cc16", gradient: "linear-gradient(135deg, #84cc16 0%, #65a30d 100%)" },
  announcements: { color: "#f59e0b", gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)" },
  api: { color: "#06b6d4", gradient: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)" },
  users: { color: "#8b5cf6", gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" },
  queues: { color: "#10b981", gradient: "linear-gradient(135deg, #10b981 0%, #047857 100%)" },
  prompts: { color: "#ec4899", gradient: "linear-gradient(135deg, #ec4899 0%, #db2777 100%)" },
  integrations: { color: "#f97316", gradient: "linear-gradient(135deg, #f97316 0%, #c2410c 100%)" },
  connections: { color: "#64748b", gradient: "linear-gradient(135deg, #64748b 0%, #475569 100%)" },
  files: { color: "#14b8a6", gradient: "linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)" },
  financial: { color: "#10b981", gradient: "linear-gradient(135deg, #10b981 0%, #065f46 100%)" },
  settings: { color: "#6366f1", gradient: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)" },
  companies: { color: "#3b82f6", gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)" },
  sales: { color: "#22c55e", gradient: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" },
  leads: { color: "#8b5cf6", gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)" },
  client: { color: "#3b82f6", gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)" },
  commission: { color: "#10b981", gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)" },
  team: { color: "#f59e0b", gradient: "linear-gradient(135deg, #f59e0b 0%, #f97316 100%)" },
  analytics: { color: "#ec4899", gradient: "linear-gradient(135deg, #ec4899 0%, #be185d 100%)" },
  facebook: { color: "#1877f2", gradient: "linear-gradient(135deg, #1877f2 0%, #0a5cbf 100%)" },
  marketplace: { color: "#0ea5e9", gradient: "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)" },
};

// ---------------- i18n helpers ----------------
// Intentionally using `i18n.t(...)` directly (no `useTranslation`) to keep the codebase style consistent.

// ---------------- theme helpers ----------------
const getPaletteMode = (theme) => theme?.palette?.mode ?? theme?.palette?.type ?? "light";
const labelColor = (theme, isActive, iconColor) =>
  isActive ? iconColor : getPaletteMode(theme) === "dark" ? "#FFF" : "#666";

// ---------------- reducer ----------------
const reducer = (state = [], action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];
    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) state[chatIndex] = chat;
        else newChats.push(chat);
      });
    }
    return [...state, ...newChats];
  }
  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);
    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    }
    return [chat, ...state];
  }
  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) state.splice(chatIndex, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
  if (action.type === "CHANGE_CHAT") {
    if (!isArray(state)) return [];
    return state.map((chat) => (chat.id === action.payload?.chat?.id ? action.payload.chat : chat));
  }
  return state;
};

// ---------------- Item de link ----------------
function ListItemLink({ icon, primary, to, showBadge, iconKey, small, collapsed }) {
  const theme = useTheme();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = activeMenu === to || location.pathname === to;
  const iconStyle = iconStyles[iconKey] || iconStyles.dashboard;

  const renderLink = React.useMemo(
    () => React.forwardRef((itemProps, ref) => <RouterLink to={to} ref={ref} {...itemProps} />),
    [to]
  );

  return (
    <Tooltip title={collapsed ? primary : ""} placement="right">
      <li>
        <ListItem
          button
          component={renderLink}
          sx={{
            borderRadius: 2,
            mx: 1,
            my: 0.5,
            minHeight: small ? 40 : 48,
            pl: collapsed ? 2 : small ? 4 : 2,
            pr: collapsed ? 2 : "auto",
            justifyContent: collapsed ? "center" : "flex-start",
            position: "relative",
            overflow: "hidden",
            background: isActive ? alpha(iconStyle.color, 0.15) : "transparent",
            "&:hover": { background: alpha(iconStyle.color, 0.1) },
            "&::before": isActive
              ? {
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  background: iconStyle.gradient,
                  borderRadius: "0 4px 4px 0",
                }
              : {},
            "& .MuiListItemText-primary": {
              color: `${labelColor(theme, isActive, iconStyle.color)} !important`,
            },
          }}
        >
          {icon && (
            <ListItemIcon
              sx={{
                minWidth: collapsed ? "auto" : 40,
                justifyContent: "center",
              }}
            >
              {showBadge ? (
                <Badge
                  badgeContent="!"
                  color="error"
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "10px",
                      height: 16,
                      minWidth: 16,
                      padding: "0 4px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: `${iconStyle.color} !important`,
                      "& svg": { color: `${iconStyle.color} !important` },
                    }}
                  >
                    {icon}
                  </Box>
                </Badge>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: `${iconStyle.color} !important`,
                    "& svg": { color: `${iconStyle.color} !important` },
                  }}
                >
                  {icon}
                </Box>
              )}
            </ListItemIcon>
          )}
          {!collapsed && (
            <ListItemText
              primary={primary}
              primaryTypographyProps={{
                sx: {
                  fontSize: "0.875rem",
                  fontWeight: isActive ? 500 : 400,
                  color: `${labelColor(theme, isActive, iconStyle.color)} !important`,
                },
              }}
            />
          )}
        </ListItem>
      </li>
    </Tooltip>
  );
}

// ---------------- Main ----------------
const MainListItems = ({ collapsed, drawerClose }) => {
  const theme = useTheme();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user, socket } = useContext(AuthContext);
  const { setActiveMenu } = useActiveMenu();
  const location = useLocation();

  // Obtener el rol del usuario
  const userRole = user?.role_name || user?.profile || 'user';

  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openCampaignSubmenu, setOpenCampaignSubmenu] = useState(false);
  const [openFlowSubmenu, setOpenFlowSubmenu] = useState(false);
  const [openDashboardSubmenu, setOpenDashboardSubmenu] = useState(false);
  // Section toggles (Ventas open by default)
  const [openSectionPrincipal, setOpenSectionPrincipal] = useState(false);
  const [openSectionVentas, setOpenSectionVentas] = useState(true);
  const [openSectionAtencion, setOpenSectionAtencion] = useState(false);
  const [openSectionMarketing, setOpenSectionMarketing] = useState(false);
  const [openSectionMarketplace, setOpenSectionMarketplace] = useState(true);
  const [openSectionIA, setOpenSectionIA] = useState(false);
  const [openSectionAdmin, setOpenSectionAdmin] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [showWhatsapp, setShowWhatsapp] = useState(true);
  const [showFacebook, setShowFacebook] = useState(true);
  const [showInstagram, setShowInstagram] = useState(true);
  const [showFlowbuilder, setShowFlowbuilder] = useState(true);
  const [showSales, setShowSales] = useState(true);
  const [showLeads, setShowLeads] = useState(true);
  const [showCommissions, setShowCommissions] = useState(true);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [showReports, setShowReports] = useState(true);
  const [invisible, setInvisible] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const { list } = useHelps();
  const [hasHelps, setHasHelps] = useState(false);

  const [, force] = useState(0);
  useEffect(() => {
    const rerender = () => force((n) => n + 1);
    i18n.on("languageChanged", rerender);
    return () => i18n.off("languageChanged", rerender);
  }, []);

  useEffect(() => {
    async function checkHelps() {
      const helps = await list();
      setHasHelps(Array.isArray(helps) && helps.length > 0);
    }
    checkHelps();
  }, [list]);

  const isManagementActive =
    location.pathname === "/" ||
    location.pathname.startsWith("/reports") ||
    location.pathname.startsWith("/moments");

  const isCampaignRouteActive =
    location.pathname === "/campaigns" ||
    location.pathname.startsWith("/contact-lists") ||
    location.pathname.startsWith("/campaigns-config");

  const isFlowbuilderRouteActive =
    location.pathname.startsWith("/phrase-lists") ||
    location.pathname.startsWith("/flowbuilders");

  const showTickets = showWhatsapp || showFacebook || showInstagram;

  useEffect(() => {
    setActiveMenu(location.pathname.startsWith("/tickets") ? "/tickets" : "");
  }, [location, setActiveMenu]);

  useEffect(() => {
    if (collapsed) {
      setOpenCampaignSubmenu(false);
      setOpenFlowSubmenu(false);
      setOpenDashboardSubmenu(false);
      setOpenSectionPrincipal(false);
      setOpenSectionVentas(false);
      setOpenSectionAtencion(false);
      setOpenSectionMarketing(false);
      setOpenSectionMarketplace(false);
      setOpenSectionIA(false);
      setOpenSectionAdmin(false);
    }
  }, [collapsed]);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    async function fetchData() {
      if (user.companyId) {
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);
        const entitlements =
          planConfigs?.entitlements ||
          planConfigs?.plan?.entitlements ||
          {};

        const allow = (key, fallback = true) => {
          if (entitlements && Object.prototype.hasOwnProperty.call(entitlements, key)) {
            return !!entitlements[key];
          }
          return fallback;
        };

        setShowCampaigns(allow("campaigns", planConfigs?.plan?.useCampaigns || false));
        setShowKanban(allow("kanban", planConfigs?.plan?.useKanban || false));
        setShowOpenAi(allow("ai", planConfigs?.plan?.useOpenAi || false));
        setShowIntegrations(allow("integrations", planConfigs?.plan?.useIntegrations || false));
        setShowSchedules(allow("schedules", planConfigs?.plan?.useSchedules || false));
        setShowInternalChat(allow("internal_chat", planConfigs?.plan?.useInternalChat || false));
        setShowExternalApi(allow("external_api", planConfigs?.plan?.useExternalApi || false));
        setShowWhatsapp(allow("whatsapp", planConfigs?.plan?.useWhatsapp || true));
        setShowFacebook(allow("facebook", planConfigs?.plan?.useFacebook || true));
        setShowInstagram(allow("instagram", planConfigs?.plan?.useInstagram || true));
        setShowFlowbuilder(allow("flowbuilder", true));
        setShowSales(allow("sales", true));
        setShowLeads(allow("leads", true));
        setShowCommissions(allow("commissions", true));
        setShowAnalytics(allow("analytics", true));
        setShowReports(allow("reports", true));
      }
    }
    fetchData();
  }, [user, getPlanCompany]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (user.id) {
      const companyId = user.companyId;
      const onCompanyChat = (data) => {
        if (["new-message", "update"].includes(data.action)) {
          dispatch({ type: "CHANGE_CHAT", payload: data });
        }
      };
      socket.on(`company-${companyId}-chat`, onCompanyChat);
      return () => {
        socket.off(`company-${companyId}-chat`, onCompanyChat);
      };
    }
  }, [socket, user.id, user.companyId]);

  useEffect(() => {
    let unreads = 0;
    for (const chat of chats) {
      for (const cu of chat.users) {
        if (cu.userId === user.id) unreads += cu.unreads;
      }
    }
    setInvisible(unreads <= 0);
  }, [chats, user.id]);

  useEffect(() => {
    const t = setTimeout(() => {
      const offline = whatsApps.some((w) =>
        ["qrcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
      );
      setConnectionWarning(offline);
    }, 2000);
    return () => clearTimeout(t);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", { params: { searchParam, pageNumber } });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const SectionHeader = ({ children }) =>
    !collapsed && (
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.75rem",
          textTransform: "uppercase",
          padding: "16px 16px 8px",
          lineHeight: 1,
          letterSpacing: "0.5px",
          color: getPaletteMode(theme) === "dark" ? "rgba(255,255,255,0.7)" : "#888",
        }}
      >
        {children}
      </Typography>
    );

  const CollapsibleSection = ({ title, iconKey, isOpen, onToggle, children }) => {
    const sectionColor = (iconStyles[iconKey] || iconStyles.dashboard).color;
    if (collapsed) return <>{children}</>;
    return (
      <>
        <ListItem
          button
          onClick={onToggle}
          sx={{
            mx: 1,
            my: 0.3,
            borderRadius: 2,
            minHeight: 36,
            "&:hover": { background: alpha(sectionColor, 0.08) },
          }}
        >
          <ListItemText
            primary={title}
            primaryTypographyProps={{
              sx: {
                fontWeight: 700,
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: isOpen ? sectionColor : (getPaletteMode(theme) === "dark" ? "rgba(255,255,255,0.7)" : "#888"),
              },
            }}
          />
          {isOpen ? (
            <ExpandLess sx={{ fontSize: 18, color: sectionColor }} />
          ) : (
            <ExpandMore sx={{ fontSize: 18, color: getPaletteMode(theme) === "dark" ? "rgba(255,255,255,0.5)" : "#aaa" }} />
          )}
        </ListItem>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      </>
    );
  };

  const SubmenuItem = (props) => <ListItemLink small {...props} />;

  const ExpandableMenuItem = ({ icon, primary, iconKey, isActive, isOpen, onToggle, children }) => {
    const iconStyle = iconStyles[iconKey] || iconStyles.dashboard;

    if (collapsed) {
      return (
        <ListItemLink
          to="#"
          primary={primary}
          icon={icon}
          iconKey={iconKey}
          collapsed={collapsed}
        />
      );
    }

    return (
      <>
        <ListItem
          button
          onClick={onToggle}
          sx={{
            borderRadius: 2,
            mx: 1,
            my: 0.5,
            minHeight: 48,
            background: isActive ? alpha(iconStyle.color, 0.15) : "transparent",
            "&:hover": { background: alpha(iconStyle.color, 0.1) },
            "& .MuiListItemText-primary": {
              color: `${labelColor(theme, isActive, iconStyle.color)} !important`,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: iconStyle.color }}>{icon}</ListItemIcon>
          <ListItemText
            primary={primary}
            primaryTypographyProps={{
              sx: {
                fontSize: "0.875rem",
                fontWeight: isActive ? 500 : 400,
                color: `${labelColor(theme, isActive, iconStyle.color)} !important`,
              },
            }}
          />
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse
          in={isOpen}
          timeout="auto"
          unmountOnExit
          sx={{
            backgroundColor: alpha(theme.palette.background.default, 0.5),
            mx: 1,
            borderRadius: 2,
          }}
        >
          <List dense component="div" disablePadding>
            {children}
          </List>
        </Collapse>
      </>
    );
  };

  const L = {
    management: i18n.t("mainDrawer.listItems.management"),
    dashboard: i18n.t("mainDrawer.listItems.dashboard"),
    reports: i18n.t("mainDrawer.listItems.reports"),
    realtime: i18n.t("mainDrawer.listItems.chatsTempoReal"),
    tickets: i18n.t("mainDrawer.listItems.tickets"),
    quick: i18n.t("mainDrawer.listItems.quickMessages"),
    kanban: i18n.t("mainDrawer.listItems.kanban"),
    contacts: i18n.t("mainDrawer.listItems.contacts"),
    schedules: i18n.t("mainDrawer.listItems.schedules"),
    tags: i18n.t("mainDrawer.listItems.tags"),
    internalChat: i18n.t("mainDrawer.listItems.chats"),
    helps: i18n.t("mainDrawer.listItems.helps"),
    admin: i18n.t("mainDrawer.listItems.administration"),
    campaigns: i18n.t("campaigns.title"),
    campaigns_list: i18n.t("campaigns.subMenus.list"),
    campaigns_contacts: i18n.t("campaigns.subMenus.listContacts"),
    campaigns_settings: i18n.t("campaigns.subMenus.settings"),
    flowbuilder: i18n.t("flowbuilder.title"),
    flowbuilder_campaign: i18n.t("flowbuilder.subMenus.campaign"),
    flowbuilder_conversation: i18n.t("flowbuilder.subMenus.conversation"),
    announcements: i18n.t("announcements.title"),
    api: i18n.t("configApis.title"),
    users: i18n.t("users.title"),
    queues: i18n.t("queues.title"),
    prompts: i18n.t("prompts.title"),
    integrations: i18n.t("mainDrawer.listItems.queueIntegration"),
    connections: i18n.t("connections.title"),
    allConnections: i18n.t("mainDrawer.listItems.allConnections"),
    files: i18n.t("mainDrawer.listItems.files"),
    financial: i18n.t("mainDrawer.listItems.financeiro"),
    subscription: i18n.t("mainDrawer.listItems.subscription"),
    settings: i18n.t("settings.title"),
    companies: i18n.t("compaies.title"),
    salesDashboard: i18n.t("mainDrawer.listItems.salesDashboard"),
    leads: i18n.t("mainDrawer.listItems.leads"),
    registerClient: i18n.t("mainDrawer.listItems.registerClient"),
    commission: i18n.t("mainDrawer.listItems.commission"),
    teamDashboard: i18n.t("mainDrawer.listItems.teamDashboard"),
    analytics: i18n.t("mainDrawer.listItems.analytics"),
    clientDashboard: i18n.t("mainDrawer.listItems.clientDashboard"),
    inbox: i18n.t("mainDrawer.listItems.inbox"),
    whatsappApi: i18n.t("mainDrawer.listItems.whatsappApi"),
    whatsappQr: i18n.t("mainDrawer.listItems.whatsappQr"),
    facebook: i18n.t("mainDrawer.listItems.facebookInstagram"),
    ai: i18n.t("mainDrawer.listItems.ai"),
    marketplace: i18n.t("marketplace.title"),
  };

  // RENDERIZADO POR ROL
  return (
    <div onClick={drawerClose}>
      {/* ============ ADMIN - Ve todo ============ */}
      {userRole === 'admin' && (
        <>
          {/* ──── PRINCIPAL ──── */}
          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.main")}
            iconKey="dashboard"
            isOpen={openSectionPrincipal}
            onToggle={() => setOpenSectionPrincipal((p) => !p)}
          >
            <Can
              role={
                (user.profile === "user" &&
                  (user.showDashboard === "enabled" || user.allowRealTime === "enabled"))
                  ? "admin"
                  : user.profile
              }
              perform={"drawer-admin-items:view"}
              yes={() => (
                <ExpandableMenuItem
                  icon={<DashboardOutlined />}
                  primary={L.management}
                  iconKey="dashboard"
                  isActive={isManagementActive}
                  isOpen={openDashboardSubmenu}
                  onToggle={() => setOpenDashboardSubmenu((p) => !p)}
                >
                  <Can
                    role={
                      user.profile === "user" && user.showDashboard === "enabled"
                        ? "admin"
                        : user.profile
                    }
                    perform={"drawer-admin-items:view"}
                    yes={() => (
                      <>
                        <SubmenuItem
                          to="/dashboard"
                          primary={L.dashboard}
                          icon={<DashboardOutlined />}
                          iconKey="dashboard"
                        />
                        <SubmenuItem
                          to="/reports"
                          primary={L.reports}
                          icon={<DescriptionRounded />}
                          iconKey="dashboard"
                        />
                      </>
                    )}
                  />
                  <Can
                    role={
                      user.profile === "user" && user.allowRealTime === "enabled"
                        ? "admin"
                        : user.profile
                    }
                    perform={"drawer-admin-items:view"}
                    yes={() => (
                      <SubmenuItem
                        to="/moments"
                        primary={L.realtime}
                        icon={<GridOnRounded />}
                        iconKey="dashboard"
                      />
                    )}
                  />
                </ExpandableMenuItem>
              )}
            />

            {showAnalytics && (
              <ListItemLink
                to="/analytics"
                primary={L.analytics}
                icon={<BarChart />}
                iconKey="analytics"
                collapsed={collapsed}
              />
            )}
          </CollapsibleSection>

          {/* ──── VENTAS (abierta por defecto) ──── */}
          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.sales")}
            iconKey="sales"
            isOpen={openSectionVentas}
            onToggle={() => setOpenSectionVentas((p) => !p)}
          >
            {showSales && (
              <ListItemLink
                to="/sales-dashboard"
                primary={L.salesDashboard}
                icon={<TrendingUp />}
                iconKey="sales"
                collapsed={collapsed}
              />
            )}

            {showLeads && (
              <ListItemLink
                to="/leads"
                primary={L.leads}
                icon={<People />}
                iconKey="leads"
                collapsed={collapsed}
              />
            )}

            {showSales && (
              <ListItemLink
                to="/register-client"
                primary={L.registerClient}
                icon={<PersonAdd />}
                iconKey="client"
                collapsed={collapsed}
              />
            )}

            {showKanban && (
              <ListItemLink
                to="/kanban"
                primary={L.kanban}
                icon={<ViewKanban />}
                iconKey="kanban"
                collapsed={collapsed}
              />
            )}

            {showCommissions && (
              <ListItemLink
                to="/commission"
                primary={L.commission}
                icon={<MonetizationOn />}
                iconKey="commission"
                collapsed={collapsed}
              />
            )}

            <ListItemLink
              to="/team-dashboard"
              primary={L.teamDashboard}
              icon={<Group />}
              iconKey="team"
              collapsed={collapsed}
            />
          </CollapsibleSection>

          {/* ──── ATENCIÓN AL CLIENTE ──── */}
          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.service")}
            iconKey="tickets"
            isOpen={openSectionAtencion}
            onToggle={() => setOpenSectionAtencion((p) => !p)}
          >
            {showTickets && (
              <ListItemLink
                to="/tickets"
                primary={L.tickets}
                icon={<WhatsApp />}
                iconKey="tickets"
                collapsed={collapsed}
              />
            )}

            {showFacebook && (
              <ListItemLink
                to="/facebook"
                primary={L.facebook}
                icon={<Facebook />}
                iconKey="facebook"
                collapsed={collapsed}
              />
            )}

            <ListItemLink
              to="/contacts"
              primary={L.contacts}
              icon={<ContactPhoneOutlined />}
              iconKey="contacts"
              collapsed={collapsed}
            />

            <ListItemLink
              to="/quick-messages"
              primary={L.quick}
              icon={<FlashOn />}
              iconKey="messages"
              collapsed={collapsed}
            />

            <ListItemLink
              to="/tags"
              primary={L.tags}
              icon={<LocalOffer />}
              iconKey="tags"
              collapsed={collapsed}
            />

            {showInternalChat && (
              <ListItemLink
                to="/chats"
                primary={L.internalChat}
                icon={
                  <Badge
                    color="secondary"
                    variant="dot"
                    invisible={invisible}
                    sx={{ "& .MuiBadge-dot": { backgroundColor: "#ef4444" } }}
                  >
                    <Forum />
                  </Badge>
                }
                iconKey="chats"
                collapsed={collapsed}
              />
            )}
          </CollapsibleSection>

          {/* ──── MARKETING ──── */}
          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.marketing")}
            iconKey="campaigns"
            isOpen={openSectionMarketing}
            onToggle={() => setOpenSectionMarketing((p) => !p)}
          >
            {showCampaigns && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ExpandableMenuItem
                    icon={<Campaign />}
                    primary={L.campaigns}
                    iconKey="campaigns"
                    isActive={isCampaignRouteActive}
                    isOpen={openCampaignSubmenu}
                    onToggle={() => setOpenCampaignSubmenu((p) => !p)}
                  >
                    <SubmenuItem
                      to="/campaigns"
                      primary={L.campaigns_list}
                      icon={<ListAlt />}
                      iconKey="campaigns"
                    />
                    <SubmenuItem
                      to="/contact-lists"
                      primary={L.campaigns_contacts}
                      icon={<People />}
                      iconKey="campaigns"
                    />
                    <SubmenuItem
                      to="/campaigns-config"
                      primary={L.campaigns_settings}
                      icon={<SettingsOutlined />}
                      iconKey="campaigns"
                    />
                  </ExpandableMenuItem>
                )}
              />
            )}

            {showSchedules && (
              <ListItemLink
                to="/schedules"
                primary={L.schedules}
                icon={<Schedule />}
                iconKey="schedules"
                collapsed={collapsed}
              />
            )}
          </CollapsibleSection>

          {/* ──── IA & AUTOMATIZACIÓN ──── */}
          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.aiAutomation")}
            iconKey="prompts"
            isOpen={openSectionIA}
            onToggle={() => setOpenSectionIA((p) => !p)}
          >
            {showOpenAi && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/prompts"
                    primary={L.prompts}
                    icon={<AllInclusiveRounded />}
                    iconKey="prompts"
                    collapsed={collapsed}
                  />
                )}
              />
            )}

            {showFlowbuilder && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ExpandableMenuItem
                    icon={<WebhookRounded />}
                    primary={L.flowbuilder}
                    iconKey="flowbuilder"
                    isActive={isFlowbuilderRouteActive}
                    isOpen={openFlowSubmenu}
                    onToggle={() => setOpenFlowSubmenu((p) => !p)}
                  >
                    <SubmenuItem
                      to="/phrase-lists"
                      primary={L.flowbuilder_campaign}
                      icon={<EventAvailable />}
                      iconKey="flowbuilder"
                    />
                    <SubmenuItem
                      to="/flowbuilders"
                      primary={L.flowbuilder_conversation}
                      icon={<ShapeLineRounded />}
                      iconKey="flowbuilder"
                    />
                  </ExpandableMenuItem>
                )}
              />
            )}

            {showIntegrations && (
              <Can
                role={user.profile}
                perform="dashboard:view"
                yes={() => (
                  <ListItemLink
                    to="/queue-integration"
                    primary={L.integrations}
                    icon={<DeviceHubRounded />}
                    iconKey="integrations"
                    collapsed={collapsed}
                  />
                )}
              />
            )}
          </CollapsibleSection>



<CollapsibleSection            title={L.marketplace}            iconKey="marketplace"            isOpen={openSectionMarketplace}            onToggle={() => setOpenSectionMarketplace((p) => !p)}          >            <ListItemLink              to="/marketplace"              primary={L.marketplace}              icon={<GridOnRounded />}              iconKey="marketplace"              collapsed={collapsed}            />          </CollapsibleSection>
          {/* ──── ADMINISTRACIÓN ──── */}
          <Can
            role={
              user.profile === "user" && user.allowConnections === "enabled"
                ? "admin"
                : user.profile
            }
            perform="dashboard:view"
            yes={() => (
              <CollapsibleSection
                title={L.admin}
                iconKey="settings"
                isOpen={openSectionAdmin}
                onToggle={() => setOpenSectionAdmin((p) => !p)}
              >
                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/users"
                      primary={L.users}
                      icon={<PeopleAltOutlined />}
                      iconKey="users"
                      collapsed={collapsed}
                    />
                  )}
                />

                <Can
                  role={
                    user.profile === "user" && user.allowConnections === "enabled"
                      ? "admin"
                      : user.profile
                  }
                  perform={"drawer-admin-items:view"}
                  yes={() => (
                    <ListItemLink
                      to="/connections"
                      primary={L.connections}
                      icon={<SignalCellularConnectedNoInternet4BarTwoTone />}
                      iconKey="connections"
                      showBadge={connectionWarning}
                      collapsed={collapsed}
                    />
                  )}
                />

                {user.super && (
                  <ListItemLink
                    to="/allConnections"
                    primary={L.allConnections}
                    icon={<PhonelinkSetupRounded />}
                    iconKey="connections"
                    collapsed={collapsed}
                  />
                )}

                {/* DATXALE_GAP3_SIDEBAR_WA_API_20260505 */}
                <Can
                  role={user.profile}
                  perform="drawer-admin-items:view"
                  yes={() => (
                    <ListItemLink
                      to="/whatsapp-api"
                      primary={L.whatsappApi}
                      icon={<WhatsApp />}
                      iconKey="connections"
                      collapsed={collapsed}
                    />
                  )}
                />

                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/queues"
                      primary={L.queues}
                      icon={<AccountTreeOutlined />}
                      iconKey="queues"
                      collapsed={collapsed}
                    />
                  )}
                />

                {showExternalApi && (
                  <Can
                    role={user.profile}
                    perform="dashboard:view"
                    yes={() => (
                      <ListItemLink
                        to="/config-apis"
                        primary={L.api}
                        icon={<CodeRounded />}
                        iconKey="api"
                        collapsed={collapsed}
                      />
                    )}
                  />
                )}

                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/files"
                      primary={L.files}
                      icon={<AttachFileRounded />}
                      iconKey="files"
                      collapsed={collapsed}
                    />
                  )}
                />

                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/financeiro"
                      primary={L.financial}
                      icon={<LocalAtm />}
                      iconKey="financial"
                      collapsed={collapsed}
                    />
                  )}
                />

                {user.super && (
                  <ListItemLink
                    to="/announcements"
                    primary={L.announcements}
                    icon={<Announcement />}
                    iconKey="announcements"
                    collapsed={collapsed}
                  />
                )}

                <Can
                  role={user.profile}
                  perform="dashboard:view"
                  yes={() => (
                    <ListItemLink
                      to="/settings"
                      primary={L.settings}
                      icon={<SettingsOutlined />}
                      iconKey="settings"
                      collapsed={collapsed}
                    />
                  )}
                />

                {user.super && (
                  <ListItemLink
                    to="/companies"
                    primary={L.companies}
                    icon={<Business />}
                    iconKey="companies"
                    collapsed={collapsed}
                  />
                )}

                {hasHelps && (
                  <ListItemLink
                    to="/helps"
                    primary={L.helps}
                    icon={<HelpOutline />}
                    iconKey="helps"
                    collapsed={collapsed}
                  />
                )}
              </CollapsibleSection>
            )}
          />
        </>
      )}

      {/* ============ VENDEDOR - Solo módulos de ventas ============ */}
      {userRole === 'vendedor' && (
        <>
          {/* ──── VENTAS ──── */}
          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.sales")}
            iconKey="sales"
            isOpen={openSectionVentas}
            onToggle={() => setOpenSectionVentas((p) => !p)}
          >
            {showSales && (
              <ListItemLink
                to="/sales-dashboard"
                primary={L.salesDashboard}
                icon={<DashboardOutlined />}
                iconKey="sales"
                collapsed={collapsed}
              />
            )}

            {showLeads && (
              <ListItemLink
                to="/leads"
                primary={L.leads}
                icon={<People />}
                iconKey="leads"
                collapsed={collapsed}
              />
            )}

            {showSales && (
              <ListItemLink
                to="/register-client"
                primary={L.registerClient}
                icon={<PersonAdd />}
                iconKey="client"
                collapsed={collapsed}
              />
            )}

            {showCommissions && (
              <ListItemLink
                to="/commission"
                primary={L.commission}
                icon={<MonetizationOn />}
                iconKey="commission"
                collapsed={collapsed}
              />
            )}
          </CollapsibleSection>


          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.tools")}
            iconKey="prompts"
            isOpen={openSectionIA}
            onToggle={() => setOpenSectionIA((p) => !p)}
          >
            {showOpenAi && (
              <ListItemLink
                to="/prompts"
                primary={L.prompts}
                icon={<SmartToy />}
                iconKey="prompts"
                collapsed={collapsed}
              />
            )}

            {showFlowbuilder && (
              <ListItemLink
                to="/flowbuilders"
                primary={L.flowbuilder}
                icon={<AccountTreeOutlined />}
                iconKey="flowbuilder"
                collapsed={collapsed}
              />
            )}

            <ListItemLink
              to="/subscription"
              primary={L.subscription}
              icon={<AllInclusiveRounded />}
              iconKey="financial"
              collapsed={collapsed}
            />
          </CollapsibleSection>
        </>
      )}

      {/* ============ SUPERVISOR - Reportes y gestión de equipo ============ */}
      {userRole === 'supervisor' && (
        <>
          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.team")}
            iconKey="team"
            isOpen={openSectionVentas}
            onToggle={() => setOpenSectionVentas((p) => !p)}
          >
            <ListItemLink
              to="/team-dashboard"
              primary={L.teamDashboard}
              icon={<Group />}
              iconKey="team"
              collapsed={collapsed}
            />

            <ListItemLink
              to="/users"
              primary={L.users}
              icon={<PeopleAltOutlined />}
              iconKey="users"
              collapsed={collapsed}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.reports")}
            iconKey="analytics"
            isOpen={openSectionPrincipal}
            onToggle={() => setOpenSectionPrincipal((p) => !p)}
          >
            {showReports && (
              <ListItemLink
                to="/reports"
                primary={L.reports}
                icon={<DescriptionRounded />}
                iconKey="dashboard"
                collapsed={collapsed}
              />
            )}

            {showAnalytics && (
              <ListItemLink
                to="/analytics"
                primary={L.analytics}
                icon={<BarChart />}
                iconKey="analytics"
                collapsed={collapsed}
              />
            )}

            {showLeads && (
              <ListItemLink
                to="/leads"
                primary={L.leads}
                icon={<People />}
                iconKey="leads"
                collapsed={collapsed}
              />
            )}
          </CollapsibleSection>

          <CollapsibleSection
            title={L.marketplace}
            iconKey="marketplace"
            isOpen={openSectionMarketplace}
            onToggle={() => setOpenSectionMarketplace((p) => !p)}
          >
            <ListItemLink
              to="/marketplace"
              primary={L.marketplace}
              icon={<GridOnRounded />}
              iconKey="marketplace"
              collapsed={collapsed}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.tools")}
            iconKey="prompts"
            isOpen={openSectionIA}
            onToggle={() => setOpenSectionIA((p) => !p)}
          >
            <ListItemLink
              to="/subscription"
              primary={L.subscription}
              icon={<AllInclusiveRounded />}
              iconKey="financial"
              collapsed={collapsed}
            />
          </CollapsibleSection>
        </>
      )}

      {/* ============ CLIENTE - Solo sus módulos ============ */}
      {userRole === 'cliente' && (
        <>
          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.main")}
            iconKey="dashboard"
            isOpen={openSectionPrincipal}
            onToggle={() => setOpenSectionPrincipal((p) => !p)}
          >
            <ListItemLink
              to="/dashboard"
              primary={L.clientDashboard}
              icon={<DashboardOutlined />}
              iconKey="dashboard"
              collapsed={collapsed}
            />

            <ListItemLink
              to="/subscription"
              primary={L.subscription}
              icon={<AllInclusiveRounded />}
              iconKey="financial"
              collapsed={collapsed}
            />

            <ListItemLink
              to="/settings"
              primary={L.settings}
              icon={<SettingsOutlined />}
              iconKey="settings"
              collapsed={collapsed}
            />

            {showReports && (
              <ListItemLink
                to="/reports"
                primary={L.reports}
                icon={<Analytics />}
                iconKey="analytics"
                collapsed={collapsed}
              />
            )}
          </CollapsibleSection>

          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.channels")}
            iconKey="tickets"
            isOpen={openSectionVentas}
            onToggle={() => setOpenSectionVentas((p) => !p)}
          >
            {showTickets && (
              <ListItemLink
                to="/tickets"
                primary={L.inbox}
                icon={<Forum />}
                iconKey="tickets"
                collapsed={collapsed}
              />
            )}

            {(showWhatsapp || showFacebook || showInstagram) && (
              <ListItemLink
                to="/config-apis"
                primary={i18n.t("configApis.title")}
                icon={<CodeRounded />}
                iconKey="api"
                collapsed={collapsed}
              />
            )}

            <ListItemLink
              to="/contacts"
              primary={L.contacts}
              icon={<ContactPhoneOutlined />}
              iconKey="contacts"
              collapsed={collapsed}
            />
          </CollapsibleSection>

          {showCampaigns && (
            <CollapsibleSection
              title={i18n.t("mainDrawer.sections.marketing")}
              iconKey="campaigns"
              isOpen={openSectionMarketing}
              onToggle={() => setOpenSectionMarketing((p) => !p)}
            >
              <ListItemLink
                to="/campaigns"
                primary={L.campaigns}
                icon={<Campaign />}
                iconKey="campaigns"
                collapsed={collapsed}
              />
            </CollapsibleSection>
          )}

          <CollapsibleSection
            title={L.marketplace}
            iconKey="marketplace"
            isOpen={openSectionMarketplace}
            onToggle={() => setOpenSectionMarketplace((p) => !p)}
          >
            <ListItemLink
              to="/marketplace"
              primary={L.marketplace}
              icon={<GridOnRounded />}
              iconKey="marketplace"
              collapsed={collapsed}
            />
          </CollapsibleSection>

          <CollapsibleSection
            title={i18n.t("mainDrawer.sections.tools")}
            iconKey="prompts"
            isOpen={openSectionIA}
            onToggle={() => setOpenSectionIA((p) => !p)}
          >
            {showOpenAi && (
              <ListItemLink
                to="/prompts"
                primary={L.ai}
                icon={<SmartToy />}
                iconKey="prompts"
                collapsed={collapsed}
              />
            )}

            {showFlowbuilder && (
              <ListItemLink
                to="/flowbuilders"
                primary={L.flowbuilder}
                icon={<AccountTreeOutlined />}
                iconKey="flowbuilder"
                collapsed={collapsed}
              />
            )}
          </CollapsibleSection>
        </>
      )}

    </div>
  );
};

export default MainListItems;
